import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  activeNamespaceField,
  fromRescanIntervalHours,
  VULNERABILITY_SCANNING_INITIAL_VALUES,
  VulnerabilityScanningFormValues,
  VulnerabilityScanningPolicyPayload,
} from './types';

const RESCAN_INTERVAL_HOURS_REGEX = /^(\d+)h$/;

export function useVulnerabilityScanningInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): VulnerabilityScanningFormValues {
  return useMemo(
    () => getInitialValuesFromPolicy(policy, templateData),
    [policy, templateData]
  );
}

export function getInitialValuesFromPolicy(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): VulnerabilityScanningFormValues {
  if (!policy) {
    if (isVulnerabilityScanningPayload(templateData)) {
      return {
        ...VULNERABILITY_SCANNING_INITIAL_VALUES,
        ...fromPayload(templateData),
      };
    }
    return VULNERABILITY_SCANNING_INITIAL_VALUES;
  }

  const base = {
    name: policy.Name || '',
    environmentGroupIds: policy.EnvironmentGroups || [],
  };

  if (!isVulnerabilityScanningPayload(policy.Data)) {
    return { ...VULNERABILITY_SCANNING_INITIAL_VALUES, ...base };
  }

  return {
    ...VULNERABILITY_SCANNING_INITIAL_VALUES,
    ...base,
    ...fromPayload(policy.Data),
  };
}

function fromPayload(data: VulnerabilityScanningPolicyPayload) {
  const mode =
    data.namespaceScope?.mode ??
    VULNERABILITY_SCANNING_INITIAL_VALUES.namespaceScopeMode;

  // The list for the other mode keeps its default, so switching mode in the form
  // offers a starting point rather than an empty list.
  return {
    ...parseRescanInterval(data.rescanInterval),
    severities: data.severities?.length
      ? data.severities
      : VULNERABILITY_SCANNING_INITIAL_VALUES.severities,
    maxConcurrentScans:
      data.maxConcurrentScans ||
      VULNERABILITY_SCANNING_INITIAL_VALUES.maxConcurrentScans,
    targetNamespace:
      data.targetNamespace ||
      VULNERABILITY_SCANNING_INITIAL_VALUES.targetNamespace,
    namespaceScopeMode: mode,
    [activeNamespaceField(mode)]: data.namespaceScope?.namespaces ?? [],
  };
}

function parseRescanInterval(interval: string) {
  const match = RESCAN_INTERVAL_HOURS_REGEX.exec(interval ?? '');
  if (!match) {
    const { rescanIntervalValue, rescanIntervalUnit } =
      VULNERABILITY_SCANNING_INITIAL_VALUES;
    return { rescanIntervalValue, rescanIntervalUnit };
  }
  return fromRescanIntervalHours(Number(match[1]));
}

function isVulnerabilityScanningPayload(
  data: unknown
): data is VulnerabilityScanningPolicyPayload {
  return typeof data === 'object' && data !== null && 'namespaceScope' in data;
}
